import React, { useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import Select from 'react-select';
import toast from 'react-hot-toast';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import MainContent from '../components/MainContent';
import Modal from '../components/Modal';
import useFetchDatav2 from '../hooks/useFetchDatav2';
import { axiosInstancev2 } from '../utils/Axios/axiosInterceptorv2';
import {
  AUDIENCE_LABELS,
  CARD,
  SMS_BASE,
  apiError,
} from '../components/sms/smsShared';
import {
  MAX_MESSAGE_LENGTH,
  estimateParts,
  hasCurlyQuotes,
  nonGsmCharacters,
  parsePhoneNumbers,
  straightenQuotes,
  unknownPlaceholders,
} from '../components/sms/smsText';

const MAX_CUSTOM_ENTRIES = 5000;
const TYPED_CONFIRM_THRESHOLD = 100;

const AUDIENCE_HELP = {
  cohorts:
    'Students in the selected cohorts, including active members through multi-cohort memberships.',
  alumni:
    'Members of cohorts that have ended. Leave the filters empty to reach all alumni.',
  certified:
    'Students with an issued certificate. Leave empty to reach all graduates.',
  custom:
    'Paste phone numbers, one per line or comma-separated. Local numbers are treated as Kenyan.',
};

const SKIPPED_LABELS = {
  missing_phone: 'No phone number',
  invalid_phone: 'Invalid phone number',
  non_kenyan: 'Not a Kenyan number',
  duplicate_phone: 'Duplicate number (sent once)',
};

const formatKes = (value) =>
  `KES ${Number(value).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

function Field({ label, hint, error, children }) {
  return (
    <div className="space-y-1.5">
      <p className="text-sm text-gray-900">{label}</p>
      {children}
      {hint && !error && <p className="text-xs text-gray-500">{hint}</p>}
      {error && <p className="text-xs text-red-700">{error}</p>}
    </div>
  );
}

function PreviewPanel({ preview, stale, isLoading, error }) {
  if (isLoading) {
    return (
      <div
        className={`${CARD} h-72 animate-pulse`}
        aria-label="Loading preview"
      />
    );
  }
  if (error) {
    return (
      <div className={`${CARD} p-5 border-red-200 bg-red-50`}>
        <p className="text-sm text-red-800">{error}</p>
      </div>
    );
  }
  if (!preview || stale) {
    return (
      <div className={`${CARD} p-6 text-center`}>
        <p className="text-sm text-gray-900">
          {stale ? 'The message or audience changed' : 'No preview yet'}
        </p>
        <p className="text-xs text-gray-500 mt-1">
          Preview to see who will receive it, how many SMS parts it takes and
          what it will cost. You must preview before sending.
        </p>
      </div>
    );
  }

  const skipped = Object.entries(preview.skipped ?? {}).filter(
    ([, count]) => count > 0
  );
  const cohorts = preview.cohorts ?? [];
  const partsRange =
    preview.segments_per_sms_min === preview.segments_per_sms_max
      ? `${preview.segments_per_sms_max}`
      : `${preview.segments_per_sms_min}–${preview.segments_per_sms_max}`;

  return (
    <div className={`${CARD} p-5 space-y-4`}>
      <div>
        <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
          Recipients
        </p>
        <p className="text-4xl font-semibold text-gray-900 tabular-nums">
          {preview.recipients.toLocaleString()}
        </p>
        <p className="text-xs text-gray-500">
          of {preview.students_matched.toLocaleString()} students matched
        </p>
      </div>

      {(preview.unknown_cohort_ids ?? []).length > 0 && (
        <div className="flex gap-2 rounded-lg bg-red-50 border border-red-200 p-3 text-xs text-red-800">
          <WarningAmberIcon fontSize="small" />
          <span>
            {preview.unknown_cohort_ids.length} selected cohort
            {preview.unknown_cohort_ids.length === 1
              ? " wasn't"
              : "s weren't"}{' '}
            found. Remove{' '}
            {preview.unknown_cohort_ids.length === 1 ? 'it' : 'them'} and
            preview again.
          </span>
        </div>
      )}
      {preview.encoding === 'UCS-2' && (
        <div className="flex gap-2 rounded-lg bg-amber-50 border border-amber-200 p-3 text-xs text-amber-800">
          <WarningAmberIcon fontSize="small" />
          <span>
            This message uses UCS-2 encoding (emoji, curly quotes or special
            characters), so each SMS part holds 70 characters instead of 160.
          </span>
        </div>
      )}

      {skipped.length > 0 && (
        <div>
          <p className="text-xs font-medium text-gray-700 mb-1">Skipped</p>
          <ul className="list-none p-0 m-0 space-y-1">
            {skipped.map(([reason, count]) => (
              <li
                key={reason}
                className="flex justify-between text-xs text-gray-600"
              >
                <span>{SKIPPED_LABELS[reason] ?? reason}</span>
                <span className="tabular-nums">{count.toLocaleString()}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {preview.sample && (
        <div>
          <p className="text-xs font-medium text-gray-700 mb-1">
            Sample · {preview.sample.mobile}
          </p>
          <div className="rounded-2xl rounded-tl-sm bg-gray-100 px-4 py-3 text-sm text-gray-900 whitespace-pre-wrap break-words">
            {preview.sample.text}
          </div>
        </div>
      )}

      <dl className="grid grid-cols-2 gap-3 text-xs">
        <div>
          <dt className="text-gray-500">SMS parts per message</dt>
          <dd className="text-gray-900 tabular-nums">
            {partsRange} ({preview.encoding})
          </dd>
        </div>
        <div>
          <dt className="text-gray-500">Total SMS parts</dt>
          <dd className="text-gray-900 tabular-nums">
            {preview.estimated_segments.toLocaleString()}
          </dd>
        </div>
        {preview.estimated_cost_kes !== null &&
          preview.estimated_cost_kes !== undefined && (
            <div>
              <dt className="text-gray-500">Estimated cost</dt>
              <dd className="text-gray-900 tabular-nums">
                {formatKes(preview.estimated_cost_kes)}
              </dd>
            </div>
          )}
      </dl>

      {cohorts.length > 0 && (
        <details className="text-xs">
          <summary className="cursor-pointer text-gray-700">
            {cohorts.length} cohort{cohorts.length === 1 ? '' : 's'} included
          </summary>
          <ul className="list-disc pl-4 mt-2 space-y-0.5 text-gray-600">
            {cohorts.map((cohort) => (
              <li key={cohort.id}>{cohort.name}</li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}

function SmsComposer() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const messageRef = useRef(null);

  const [audienceType, setAudienceType] = useState('cohorts');
  const [cohortIds, setCohortIds] = useState([]);
  const [alumniYears, setAlumniYears] = useState([]);
  const [alumniCohortIds, setAlumniCohortIds] = useState([]);
  const [certifiedCohortIds, setCertifiedCohortIds] = useState([]);
  const [phoneText, setPhoneText] = useState('');
  const [message, setMessage] = useState('');
  const [fallbackName, setFallbackName] = useState('Learner');

  const [preview, setPreview] = useState(null); // { key, data }
  const [previewError, setPreviewError] = useState(null); // { key, message }
  const [notice, setNotice] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [typedCount, setTypedCount] = useState('');
  const [duplicate, setDuplicate] = useState(false);
  const [sendError, setSendError] = useState(null); // { message, mayExist }

  const { data: cohortsData } = useFetchDatav2(
    ['cohorts'],
    '/cohorts',
    {},
    "Couldn't get cohorts. Please try again!",
    true
  );
  const cohorts = useMemo(
    () => cohortsData?.data?.cohorts ?? [],
    [cohortsData]
  );

  const today = new Date().toISOString().slice(0, 10);
  const endedCohorts = cohorts.filter(
    (cohort) => cohort.end_date && cohort.end_date.slice(0, 10) < today
  );
  const toOption = (cohort) => ({
    value: cohort.id,
    label: `${cohort.name} (${cohort.no_of_students || 0} students)`,
  });
  const cohortOptions = cohorts.map(toOption);
  const yearOptions = [
    ...new Set(
      endedCohorts.map((cohort) => Number(cohort.end_date.slice(0, 4)))
    ),
  ]
    .sort((a, b) => b - a)
    .map((year) => ({ value: year, label: String(year) }));
  const alumniCohortOptions = endedCohorts
    .filter(
      (cohort) =>
        alumniYears.length === 0 ||
        alumniYears.includes(Number(cohort.end_date.slice(0, 4)))
    )
    .map(toOption);
  const optionsFor = (options, ids) =>
    options.filter((option) => ids.includes(option.value));

  const phoneNumbers = parsePhoneNumbers(phoneText);

  // Only the fields each audience type accepts; anything else is a 400.
  const audience = useMemo(() => {
    if (audienceType === 'cohorts')
      return { type: 'cohorts', cohort_ids: cohortIds };
    if (audienceType === 'alumni') {
      return {
        type: 'alumni',
        ...(alumniYears.length ? { years: alumniYears } : {}),
        ...(alumniCohortIds.length ? { cohort_ids: alumniCohortIds } : {}),
      };
    }
    if (audienceType === 'certified') {
      return {
        type: 'certified',
        ...(certifiedCohortIds.length
          ? { cohort_ids: certifiedCohortIds }
          : {}),
      };
    }
    return { type: 'custom', phone_numbers: phoneNumbers };
    // phoneNumbers is derived from phoneText
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    audienceType,
    cohortIds,
    alumniYears,
    alumniCohortIds,
    certifiedCohortIds,
    phoneText,
  ]);

  const requestBody = {
    audience,
    message,
    ...(fallbackName.trim()
      ? { fallback_first_name: fallbackName.trim() }
      : {}),
  };
  const requestKey = JSON.stringify(requestBody);
  const currentPreview = preview?.key === requestKey ? preview.data : null;
  const currentPreviewError =
    previewError?.key === requestKey ? previewError.message : null;

  // Client-side checks; the server is the final word.
  const estimate = estimateParts(message);
  const badPlaceholders = unknownPlaceholders(message);
  const specialChars = nonGsmCharacters(message);
  let audienceError = null;
  if (audienceType === 'cohorts' && cohortIds.length === 0) {
    audienceError = 'Select at least one cohort.';
  }
  if (audienceType === 'custom') {
    if (phoneNumbers.length === 0)
      audienceError = 'Add at least one phone number.';
    if (phoneNumbers.length > MAX_CUSTOM_ENTRIES) {
      audienceError = `A custom list can have at most ${MAX_CUSTOM_ENTRIES.toLocaleString()} numbers.`;
    }
  }
  let messageError = null;
  if (message.length > MAX_MESSAGE_LENGTH) {
    messageError = `The message is too long: ${MAX_MESSAGE_LENGTH} characters at most.`;
  } else if (badPlaceholders.length) {
    messageError = `Unknown placeholder ${badPlaceholders
      .map((p) => `{{${p}}}`)
      .join(', ')}. Use {{first_name}} or {{last_name}}.`;
  }
  const canPreview =
    !audienceError && !messageError && message.trim().length > 0;

  const previewMutation = useMutation(
    (body) => axiosInstancev2.post(`${SMS_BASE}/preview`, body),
    {
      onMutate: () => {
        setNotice('');
        setSendError(null);
      },
      onSuccess: (response, body) =>
        setPreview({ key: JSON.stringify(body), data: response.data }),
      onError: (error, body) => {
        const status = error?.response?.status;
        let text = apiError(
          error,
          "We couldn't preview this message. Please try again."
        );
        if (status === 403) text = 'Ask a SuperAdmin for SMS access.';
        if (status === 404)
          text = "SMS campaigns aren't available on the server yet.";
        setPreviewError({ key: JSON.stringify(body), message: text });
      },
    }
  );

  const sendMutation = useMutation(
    (body) => axiosInstancev2.post(SMS_BASE, body),
    {
      onMutate: () => setSendError(null),
      onSuccess: (response) => {
        queryClient.invalidateQueries(['sms-campaigns']);
        toast.success('SMS campaign queued.');
        navigate(`/sms/${response.data.id}`);
      },
      onError: (error) => {
        const status = error?.response?.status;
        const body = error?.response?.data;
        if (status === 409 && body?.preview) {
          // The audience changed since the preview: show the new numbers.
          setPreview({ key: requestKey, data: body.preview });
          setConfirmOpen(false);
          setTypedCount('');
          setNotice(
            'The audience changed since your preview. Check the new recipient count and confirm again.'
          );
          return;
        }
        if (status === 409) {
          setDuplicate(true);
          return;
        }
        if (status === 503) {
          setSendError({ message: "SMS sending isn't switched on yet." });
        } else if (status === 502) {
          setSendError({
            message:
              "The server didn't respond in time. The campaign may have been created, so check the SMS history before trying again.",
            mayExist: true,
          });
        } else if (status === 500) {
          setSendError({
            message:
              "Something went wrong. Nothing was created; it's safe to try again.",
          });
        } else {
          setSendError({
            message: apiError(error, "We couldn't send this campaign."),
          });
        }
      },
    }
  );

  let sendBlocked = null;
  if (!currentPreview) sendBlocked = 'Preview the message before sending.';
  else if (!currentPreview.sending_enabled)
    sendBlocked = "SMS sending isn't switched on yet.";
  else if (currentPreview.recipients === 0)
    sendBlocked = 'No one in this audience can receive an SMS.';
  else if (currentPreview.recipients > currentPreview.max_recipients) {
    sendBlocked = `Too many recipients: the limit is ${currentPreview.max_recipients.toLocaleString()} per campaign.`;
  } else if ((currentPreview.unknown_cohort_ids ?? []).length) {
    sendBlocked = "Some selected cohorts weren't found.";
  }

  const needsTypedConfirm =
    currentPreview?.recipients > TYPED_CONFIRM_THRESHOLD;
  const typedMatches =
    !needsTypedConfirm ||
    Number(typedCount.replace(/[,\s]/g, '')) === currentPreview?.recipients;

  const send = (allowDuplicate) =>
    sendMutation.mutate({
      ...requestBody,
      confirm_recipient_count: currentPreview.recipients,
      ...(allowDuplicate ? { allow_duplicate: true } : {}),
    });

  const insertPlaceholder = (name) => {
    const textarea = messageRef.current;
    const token = `{{${name}}}`;
    const start = textarea?.selectionStart ?? message.length;
    const end = textarea?.selectionEnd ?? message.length;
    setMessage(message.slice(0, start) + token + message.slice(end));
    requestAnimationFrame(() => {
      if (!textarea) return;
      textarea.focus();
      textarea.setSelectionRange(start + token.length, start + token.length);
    });
  };

  const openConfirm = () => {
    setTypedCount('');
    setDuplicate(false);
    setSendError(null);
    setConfirmOpen(true);
  };

  return (
    <MainContent full>
      <div className="space-y-5 pb-6">
        <div className={`${CARD} px-5 py-4`}>
          <nav className="text-xs text-gray-500 mb-1">
            <Link to="/sms" className="hover:text-claret-500">
              Bulk SMS
            </Link>{' '}
            › <span className="text-gray-900">New SMS</span>
          </nav>
          <h1 className="text-lg font-semibold text-gray-900">New SMS</h1>
          <p className="text-xs text-gray-500">
            Choose who receives it, write the message, preview, then confirm.
            Messages send immediately and can&apos;t be cancelled.
          </p>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-5 gap-5 items-start">
          <div className="xl:col-span-3 space-y-5">
            <section className={`${CARD} p-5 space-y-4`}>
              <h2 className="text-base font-semibold text-gray-900">
                Audience
              </h2>
              <div
                className="grid grid-cols-2 lg:grid-cols-4 gap-2"
                role="radiogroup"
              >
                {Object.entries(AUDIENCE_LABELS).map(([type, label]) => (
                  <button
                    key={type}
                    type="button"
                    role="radio"
                    aria-checked={audienceType === type}
                    onClick={() => setAudienceType(type)}
                    className={`rounded-lg border px-3 py-2 text-sm text-left transition-colors ${
                      audienceType === type
                        ? 'border-claret-500 bg-[#FBEFF3] text-claret-500'
                        : 'border-gray-200 text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
              <p className="text-xs text-gray-500">
                {AUDIENCE_HELP[audienceType]}
              </p>

              {audienceType === 'cohorts' && (
                <Field
                  label="Cohorts"
                  hint="Required"
                  error={message.trim() ? audienceError : null}
                >
                  <Select
                    isMulti
                    placeholder="Select cohorts"
                    classNamePrefix="dropdown"
                    options={cohortOptions}
                    value={optionsFor(cohortOptions, cohortIds)}
                    onChange={(selected) =>
                      setCohortIds(selected.map((o) => o.value))
                    }
                  />
                </Field>
              )}
              {audienceType === 'alumni' && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <Field label="Year the cohort ended" hint="Optional">
                    <Select
                      isMulti
                      placeholder="All years"
                      classNamePrefix="dropdown"
                      options={yearOptions}
                      value={yearOptions.filter((o) =>
                        alumniYears.includes(o.value)
                      )}
                      onChange={(selected) =>
                        setAlumniYears(selected.map((o) => o.value))
                      }
                    />
                  </Field>
                  <Field label="Only these ended cohorts" hint="Optional">
                    <Select
                      isMulti
                      placeholder="All ended cohorts"
                      classNamePrefix="dropdown"
                      options={alumniCohortOptions}
                      value={optionsFor(alumniCohortOptions, alumniCohortIds)}
                      onChange={(selected) =>
                        setAlumniCohortIds(selected.map((o) => o.value))
                      }
                    />
                  </Field>
                </div>
              )}
              {audienceType === 'certified' && (
                <Field label="Only graduates of these cohorts" hint="Optional">
                  <Select
                    isMulti
                    placeholder="All graduates"
                    classNamePrefix="dropdown"
                    options={cohortOptions}
                    value={optionsFor(cohortOptions, certifiedCohortIds)}
                    onChange={(selected) =>
                      setCertifiedCohortIds(selected.map((o) => o.value))
                    }
                  />
                </Field>
              )}
              {audienceType === 'custom' && (
                <Field
                  label="Phone numbers"
                  error={phoneText ? audienceError : null}
                  hint={`${phoneNumbers.length.toLocaleString()} number${
                    phoneNumbers.length === 1 ? '' : 's'
                  } · ${MAX_CUSTOM_ENTRIES.toLocaleString()} at most`}
                >
                  <textarea
                    rows={6}
                    value={phoneText}
                    onChange={(event) => setPhoneText(event.target.value)}
                    placeholder={'0712345678\n+254 722 000 111'}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-claret-500"
                  />
                </Field>
              )}
            </section>

            <section className={`${CARD} p-5 space-y-4`}>
              <div className="flex items-center justify-between gap-3">
                <h2 className="text-base font-semibold text-gray-900">
                  Message
                </h2>
                <div className="flex gap-2">
                  {['first_name', 'last_name'].map((name) => (
                    <button
                      key={name}
                      type="button"
                      onClick={() => insertPlaceholder(name)}
                      className="rounded-md border border-gray-300 px-2 py-1 text-xs font-mono text-gray-700 hover:border-claret-500 hover:text-claret-500"
                    >
                      + {`{{${name}}}`}
                    </button>
                  ))}
                </div>
              </div>
              <textarea
                ref={messageRef}
                rows={6}
                value={message}
                onChange={(event) => setMessage(event.target.value)}
                placeholder="Hi {{first_name}}, ..."
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-claret-500"
              />
              <div className="flex flex-wrap justify-between gap-2 text-xs">
                <span
                  className={
                    message.length > MAX_MESSAGE_LENGTH
                      ? 'text-red-700'
                      : 'text-gray-500'
                  }
                >
                  {message.length.toLocaleString()} / {MAX_MESSAGE_LENGTH}{' '}
                  characters
                </span>
                {estimate.parts > 0 && (
                  <span className="text-gray-500">
                    About {estimate.parts} SMS part
                    {estimate.parts === 1 ? '' : 's'} per message (
                    {estimate.encoding}), estimated with a typical name
                  </span>
                )}
              </div>
              {messageError && (
                <p className="text-xs text-red-700">{messageError}</p>
              )}
              {specialChars.length > 0 && (
                <div className="flex flex-wrap items-center gap-2 rounded-lg bg-amber-50 border border-amber-200 p-3 text-xs text-amber-800">
                  <WarningAmberIcon fontSize="small" />
                  <span className="flex-1">
                    {specialChars.slice(0, 8).join(' ')} switch
                    {specialChars.length === 1 ? 'es' : ''} the message to
                    UCS-2, so each SMS part fits 70 characters instead of 160.
                  </span>
                  {hasCurlyQuotes(message) && (
                    <button
                      type="button"
                      onClick={() => setMessage(straightenQuotes(message))}
                      className="rounded-md border border-amber-300 bg-white px-2 py-1 text-amber-800 hover:bg-amber-100"
                    >
                      Use straight quotes
                    </button>
                  )}
                </div>
              )}
              <Field
                label="Name to use when a first name is missing"
                hint="Used for numbers without a student profile, such as a custom list."
              >
                <input
                  type="text"
                  value={fallbackName}
                  onChange={(event) => setFallbackName(event.target.value)}
                  className="w-full sm:w-64 rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-claret-500"
                />
              </Field>
            </section>
          </div>

          <div className="xl:col-span-2 space-y-4 xl:sticky xl:top-6">
            {notice && (
              <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-xs text-amber-800">
                {notice}
              </div>
            )}
            <PreviewPanel
              preview={currentPreview}
              stale={Boolean(preview) && !currentPreview}
              isLoading={previewMutation.isLoading}
              error={currentPreviewError}
            />
            {sendError && !confirmOpen && (
              <div className="rounded-lg bg-red-50 border border-red-200 p-3 text-xs text-red-800">
                {sendError.message}{' '}
                {sendError.mayExist && (
                  <Link to="/sms" className="underline">
                    Open SMS history
                  </Link>
                )}
              </div>
            )}
            <div className="flex flex-col gap-2">
              <button
                type="button"
                disabled={!canPreview || previewMutation.isLoading}
                onClick={() => previewMutation.mutate(requestBody)}
                className="w-full rounded-md border border-claret-500 px-4 py-2 text-sm text-claret-500 hover:bg-[#FBEFF3] disabled:opacity-40 disabled:hover:bg-transparent"
              >
                {previewMutation.isLoading ? 'Previewing…' : 'Preview'}
              </button>
              <button
                type="button"
                disabled={Boolean(sendBlocked)}
                onClick={openConfirm}
                className="w-full rounded-md bg-claret-500 px-4 py-2 text-sm text-white hover:bg-claret-400 disabled:opacity-40 disabled:hover:bg-claret-500"
              >
                Send
              </button>
              {sendBlocked && (
                <p className="text-xs text-gray-500 text-center">
                  {sendBlocked}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>

      {currentPreview && (
        <Modal
          modalOpen={confirmOpen}
          small
          title="Send this SMS?"
          onClose={() => !sendMutation.isLoading && setConfirmOpen(false)}
        >
          <div className="space-y-4 text-sm">
            <dl className="grid grid-cols-2 gap-3">
              <div>
                <dt className="text-xs text-gray-500">Recipients</dt>
                <dd className="text-lg font-semibold tabular-nums">
                  {currentPreview.recipients.toLocaleString()}
                </dd>
              </div>
              <div>
                <dt className="text-xs text-gray-500">Total SMS parts</dt>
                <dd className="text-lg font-semibold tabular-nums">
                  {currentPreview.estimated_segments.toLocaleString()}
                </dd>
              </div>
              {currentPreview.estimated_cost_kes !== null &&
                currentPreview.estimated_cost_kes !== undefined && (
                  <div>
                    <dt className="text-xs text-gray-500">Estimated cost</dt>
                    <dd className="font-semibold tabular-nums">
                      {formatKes(currentPreview.estimated_cost_kes)}
                    </dd>
                  </div>
                )}
            </dl>
            {currentPreview.sample && (
              <div className="rounded-2xl rounded-tl-sm bg-gray-100 px-4 py-3 whitespace-pre-wrap break-words">
                {currentPreview.sample.text}
              </div>
            )}
            <p className="text-xs text-gray-500">
              Messages start sending immediately and can&apos;t be cancelled.
            </p>
            {needsTypedConfirm && !duplicate && (
              <label className="block space-y-1">
                <span className="text-xs text-gray-700">
                  Type {currentPreview.recipients.toLocaleString()} to confirm
                </span>
                <input
                  type="text"
                  inputMode="numeric"
                  value={typedCount}
                  onChange={(event) => setTypedCount(event.target.value)}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm tabular-nums focus:outline-none focus:ring-2 focus:ring-claret-500"
                />
              </label>
            )}
            {duplicate && (
              <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-xs text-amber-800">
                You sent this a few minutes ago.
              </div>
            )}
            {sendError && (
              <div className="rounded-lg bg-red-50 border border-red-200 p-3 text-xs text-red-800">
                {sendError.message}
              </div>
            )}
            <div className="flex justify-end gap-2">
              <button
                type="button"
                disabled={sendMutation.isLoading}
                onClick={() => setConfirmOpen(false)}
                className="rounded-md border border-gray-300 px-4 py-2 text-sm text-gray-700"
              >
                Cancel
              </button>
              {duplicate ? (
                <button
                  type="button"
                  disabled={sendMutation.isLoading}
                  onClick={() => send(true)}
                  className="rounded-md bg-claret-500 px-4 py-2 text-sm text-white disabled:opacity-40"
                >
                  {sendMutation.isLoading ? 'Sending…' : 'Send again anyway'}
                </button>
              ) : (
                <button
                  type="button"
                  disabled={!typedMatches || sendMutation.isLoading}
                  onClick={() => send(false)}
                  className="rounded-md bg-claret-500 px-4 py-2 text-sm text-white disabled:opacity-40"
                >
                  {sendMutation.isLoading
                    ? 'Sending…'
                    : `Send to ${currentPreview.recipients.toLocaleString()}`}
                </button>
              )}
            </div>
          </div>
        </Modal>
      )}
    </MainContent>
  );
}

export default SmsComposer;
